import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import DashboardFooter from '../layouts/dashboard_footer';
import { buildApiUrl, useBaseUrl } from '../../utils/utils';
import localforage from 'localforage';

interface Stakeholder {
    id: number;
    name: string;
    type: string;
    category: string;
    location: string;
    services: string[];
    description: string;
    contact_phone: string;
    contact_email: string;
    active: boolean;
    request_count: number;
}

interface StakeholderRequest {
    id: number;
    stakeholder_id: number;
    stakeholder_name: string;
    service_type: string;
    description: string;
    priority: 'low' | 'medium' | 'high';
    status: 'pending' | 'approved' | 'rejected' | 'completed';
    created: string;
}

const stakeholderCategories = [
    { id: 'supplier', name: 'Supplier', icon: 'ri-tools-line', color: 'success' },
    { id: 'regulator', name: 'Regulator', icon: 'ri-government-line', color: 'info' },
    { id: 'research', name: 'Research', icon: 'ri-microscope-line', color: 'warning' },
    { id: 'technology', name: 'Technology', icon: 'ri-computer-line', color: 'secondary' },
    { id: 'insurance', name: 'Insurance', icon: 'ri-shield-check-line', color: 'danger' },
    { id: 'other', name: 'Other', icon: 'ri-more-line', color: 'primary' },
];

const StakeholderCenter: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [staffId, setStaffId] = useState<number | null>(null);

    const [activeTab, setActiveTab] = useState<'search' | 'requests' | 'management'>('search');
    const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
    const [requests, setRequests] = useState<StakeholderRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState('');

    const [showAddStakeholder, setShowAddStakeholder] = useState(false);
    const [addForm, setAddForm] = useState({ name: '', type: 'supplier', category: '', location: '', description: '', contact_phone: '', contact_email: '', services: '' });
    const [submitting, setSubmitting] = useState(false);

    const [showRequestFor, setShowRequestFor] = useState<Stakeholder | null>(null);
    const [requestForm, setRequestForm] = useState({ service_type: '', description: '', priority: 'medium' });

    useEffect(() => {
        localforage.getItem("clinic_id").then((cid: any) => setClinicId(cid ? Number(cid) : null));
        localforage.getItem("staff_id").then((sid: any) => setStaffId(sid ? Number(sid) : null));
    }, []);

    const loadStakeholders = async () => {
        if (!clinicId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ clinic_id: String(clinicId), search: searchQuery, type: selectedType });
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholders/?${params}`));
            const data = await res.json();
            if (data.status === 'success') setStakeholders(data.data || []);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load stakeholders' });
        } finally {
            setLoading(false);
        }
    };

    const loadRequests = async () => {
        if (!clinicId) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholder-requests/?clinic_id=${clinicId}`));
            const data = await res.json();
            if (data.status === 'success') setRequests(data.data || []);
        } catch { /* leave requests as-is on failure */ }
    };

    useEffect(() => { loadStakeholders(); loadRequests(); }, [clinicId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadStakeholders(); }, [searchQuery, selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitStakeholder = async () => {
        if (!addForm.name.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Field', text: 'Name is required.' });
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholders/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clinic_id: clinicId, staff_id: staffId, ...addForm,
                    services: addForm.services.split(',').map(s => s.trim()).filter(Boolean),
                }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Stakeholder Added', timer: 1500, showConfirmButton: false });
                setShowAddStakeholder(false);
                setAddForm({ name: '', type: 'supplier', category: '', location: '', description: '', contact_phone: '', contact_email: '', services: '' });
                loadStakeholders();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to add stakeholder.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while adding stakeholder.' });
        } finally {
            setSubmitting(false);
        }
    };

    const submitRequest = async () => {
        if (!showRequestFor || !requestForm.service_type.trim() || !requestForm.description.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Service type and description are required.' });
            return;
        }
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholder-requests/`), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ clinic_id: clinicId, staff_id: staffId, stakeholder_id: showRequestFor.id, ...requestForm }),
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Request Submitted', timer: 1500, showConfirmButton: false });
                setShowRequestFor(null);
                setRequestForm({ service_type: '', description: '', priority: 'medium' });
                loadRequests();
                loadStakeholders();
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'Failed to submit request.' });
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Network error while submitting request.' });
        }
    };

    const cancelRequest = async (req: StakeholderRequest) => {
        const confirm = await Swal.fire({ title: 'Cancel this request?', icon: 'question', showCancelButton: true, confirmButtonText: 'Yes, cancel it' });
        if (!confirm.isConfirmed) return;
        try {
            const res = await fetch(buildApiUrl(baseUrl, `facilities/stakeholder-requests/${req.id}/status`), {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'rejected' }),
            });
            if (res.ok) loadRequests();
        } catch { /* best-effort */ }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />

            <div className="vertical-overlay"></div>

            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                    <h4 className="mb-sm-0">Stakeholder Center</h4>
                                    <div className="page-title-right">
                                        <ol className="breadcrumb m-0">
                                            <li className="breadcrumb-item"><a href="/institution/home">Institution</a></li>
                                            <li className="breadcrumb-item active">Stakeholder Center</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-12">
                                <ul className="nav nav-pills nav-justified" role="tablist">
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>
                                            <i className="ri-search-line me-2"></i>Find Stakeholders
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'requests' ? 'active' : ''}`} onClick={() => setActiveTab('requests')}>
                                            <i className="ri-file-list-line me-2"></i>My Requests
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'management' ? 'active' : ''}`} onClick={() => setActiveTab('management')}>
                                            <i className="ri-settings-line me-2"></i>Overview
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            {activeTab === 'search' && (
                                <motion.div key="search" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <div className="card">
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-4">
                                                            <input type="text" className="form-control" placeholder="Search stakeholders..."
                                                                   value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                                        </div>
                                                        <div className="col-md-4">
                                                            <select className="form-select" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                                                                <option value="">All Types</option>
                                                                {stakeholderCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-4 text-end">
                                                            <button className="btn btn-primary" onClick={() => setShowAddStakeholder(true)}>
                                                                <i className="ri-add-line me-1"></i>Add Stakeholder
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-12">
                                            <h5 className="mb-3">Your Stakeholders</h5>
                                            {loading ? (
                                                <div className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                                                </div>
                                            ) : stakeholders.length === 0 ? (
                                                <div className="text-center py-5">
                                                    <i className="ri-building-line" style={{ fontSize: '2.5rem' }}></i>
                                                    <h5 className="mt-3">No stakeholders added yet</h5>
                                                    <p className="text-muted">Add suppliers, regulators, research partners, and other external
                                                        organizations your clinic works with to track your relationships and requests.</p>
                                                    <button className="btn btn-primary" onClick={() => setShowAddStakeholder(true)}>
                                                        <i className="ri-add-line me-1"></i>Add Your First Stakeholder
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="row g-4">
                                                    {stakeholders.map(stakeholder => (
                                                        <div key={stakeholder.id} className="col-lg-6">
                                                            <div className="card h-100">
                                                                <div className="card-body">
                                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                                        <div>
                                                                            <h5 className="card-title mb-1">{stakeholder.name}</h5>
                                                                            <p className="text-muted mb-2 text-capitalize">{stakeholder.type}{stakeholder.category ? ` · ${stakeholder.category}` : ''}</p>
                                                                            {stakeholder.location && (
                                                                                <div className="d-flex align-items-center text-muted">
                                                                                    <i className="ri-map-pin-line me-1"></i>
                                                                                    <small>{stakeholder.location}</small>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className="badge bg-light text-dark">{stakeholder.request_count} request{stakeholder.request_count === 1 ? '' : 's'}</span>
                                                                    </div>
                                                                    {stakeholder.description && <p className="card-text text-muted mb-3">{stakeholder.description}</p>}
                                                                    {stakeholder.services.length > 0 && (
                                                                        <div className="mb-3">
                                                                            <h6 className="mb-2">Services:</h6>
                                                                            <div className="d-flex flex-wrap gap-1">
                                                                                {stakeholder.services.map((service, index) => (
                                                                                    <span key={index} className="badge bg-light text-dark">{service}</span>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                        <div>
                                                                            {stakeholder.contact_phone && (<><i className="ri-phone-line me-1"></i><small className="text-muted">{stakeholder.contact_phone}</small></>)}
                                                                        </div>
                                                                        <button className="btn btn-primary btn-sm" onClick={() => setShowRequestFor(stakeholder)}>
                                                                            <i className="ri-add-line me-1"></i>Request Service
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeTab === 'requests' && (
                                <motion.div key="requests" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                                    <div className="row mb-4">
                                        {[
                                            { label: 'Pending', status: 'pending', color: 'warning', icon: 'ri-time-line' },
                                            { label: 'Approved', status: 'approved', color: 'success', icon: 'ri-check-line' },
                                            { label: 'Completed', status: 'completed', color: 'primary', icon: 'ri-check-double-line' },
                                            { label: 'Rejected', status: 'rejected', color: 'danger', icon: 'ri-close-line' },
                                        ].map(s => (
                                            <div className="col-lg-3 col-md-6" key={s.status}>
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-body">
                                                        <div className="d-flex align-items-center">
                                                            <div className="flex-shrink-0">
                                                                <div className={`avatar-sm rounded-circle bg-${s.color}-subtle text-${s.color} d-flex align-items-center justify-content-center`}>
                                                                    <i className={`${s.icon} fs-18`}></i>
                                                                </div>
                                                            </div>
                                                            <div className="flex-grow-1 ms-3">
                                                                <h6 className="mb-1">{s.label}</h6>
                                                                <h4 className="mb-0">{requests.filter(r => r.status === s.status).length}</h4>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="row">
                                        <div className="col-12">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-transparent border-0">
                                                    <h5 className="card-title mb-0">My Stakeholder Requests</h5>
                                                </div>
                                                <div className="card-body p-0">
                                                    {requests.length === 0 ? (
                                                        <div className="text-center py-5">
                                                            <div className="avatar-lg mx-auto mb-4">
                                                                <div className="avatar-title bg-light text-primary rounded-circle fs-24">
                                                                    <i className="ri-file-list-line"></i>
                                                                </div>
                                                            </div>
                                                            <h5>No Requests Found</h5>
                                                            <p className="text-muted">Request a service from a stakeholder to see it here.</p>
                                                        </div>
                                                    ) : (
                                                        <div className="table-responsive">
                                                            <table className="table table-hover align-middle mb-0">
                                                                <thead className="table-light">
                                                                    <tr><th>Request Details</th><th>Stakeholder</th><th>Priority</th><th>Status</th><th>Date</th><th>Actions</th></tr>
                                                                </thead>
                                                                <tbody>
                                                                    {requests.map(request => (
                                                                        <tr key={request.id}>
                                                                            <td>
                                                                                <h6 className="mb-1">{request.service_type}</h6>
                                                                                <p className="text-muted mb-0 small">{request.description}</p>
                                                                            </td>
                                                                            <td>{request.stakeholder_name}</td>
                                                                            <td>
                                                                                <span className={`badge bg-${request.priority === 'high' ? 'danger' : request.priority === 'medium' ? 'warning' : 'success'}-subtle text-${request.priority === 'high' ? 'danger' : request.priority === 'medium' ? 'warning' : 'success'} text-capitalize`}>
                                                                                    {request.priority}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge bg-${request.status === 'approved' ? 'success' : request.status === 'rejected' ? 'danger' : request.status === 'completed' ? 'primary' : 'warning'}-subtle text-${request.status === 'approved' ? 'success' : request.status === 'rejected' ? 'danger' : request.status === 'completed' ? 'primary' : 'warning'} text-capitalize`}>
                                                                                    {request.status}
                                                                                </span>
                                                                            </td>
                                                                            <td>{new Date(request.created).toLocaleDateString()}</td>
                                                                            <td>
                                                                                {request.status === 'pending' && (
                                                                                    <button className="btn btn-sm btn-outline-danger" title="Cancel Request" onClick={() => cancelRequest(request)}>
                                                                                        <i className="ri-close-line"></i>
                                                                                    </button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {activeTab === 'management' && (
                                <motion.div key="management" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }}>
                                    <div className="row mb-4">
                                        <div className="col-lg-8">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-transparent border-0">
                                                    <h5 className="card-title mb-0">Stakeholder Overview</h5>
                                                    <p className="text-muted mb-0">Your clinic's external partner relationships</p>
                                                </div>
                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center p-3 bg-light rounded">
                                                                <div className="avatar-sm me-3">
                                                                    <div className="avatar-title bg-primary-subtle text-primary rounded-circle"><i className="ri-group-line"></i></div>
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1">Stakeholders on File</h6>
                                                                    <h4 className="mb-0 text-primary">{stakeholders.length}</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center p-3 bg-light rounded">
                                                                <div className="avatar-sm me-3">
                                                                    <div className="avatar-title bg-success-subtle text-success rounded-circle"><i className="ri-handshake-line"></i></div>
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-1">Completed Requests</h6>
                                                                    <h4 className="mb-0 text-success">{requests.filter(r => r.status === 'completed').length}</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-4">
                                            <div className="card border-0 shadow-sm">
                                                <div className="card-header bg-transparent border-0"><h6 className="card-title mb-0">Quick Actions</h6></div>
                                                <div className="card-body">
                                                    <div className="d-grid gap-2">
                                                        <button className="btn btn-primary" onClick={() => { setActiveTab('search'); setShowAddStakeholder(true); }}>
                                                            <i className="ri-add-line me-2"></i>Add New Stakeholder
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>

            <DashboardFooter />

            {showAddStakeholder && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Stakeholder</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddStakeholder(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input type="text" className="form-control" value={addForm.name} onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Type</label>
                                        <select className="form-select" value={addForm.type} onChange={(e) => setAddForm(f => ({ ...f, type: e.target.value }))}>
                                            {stakeholderCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Category</label>
                                        <input type="text" className="form-control" placeholder="e.g. Drug Manufacturing" value={addForm.category} onChange={(e) => setAddForm(f => ({ ...f, category: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Location</label>
                                    <input type="text" className="form-control" value={addForm.location} onChange={(e) => setAddForm(f => ({ ...f, location: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Services (comma-separated)</label>
                                    <input type="text" className="form-control" placeholder="e.g. Drug Supply, Clinical Trials" value={addForm.services} onChange={(e) => setAddForm(f => ({ ...f, services: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Contact Phone</label>
                                        <input type="text" className="form-control" value={addForm.contact_phone} onChange={(e) => setAddForm(f => ({ ...f, contact_phone: e.target.value }))} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Contact Email</label>
                                        <input type="email" className="form-control" value={addForm.contact_email} onChange={(e) => setAddForm(f => ({ ...f, contact_email: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={3} value={addForm.description} onChange={(e) => setAddForm(f => ({ ...f, description: e.target.value }))}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowAddStakeholder(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitStakeholder}>{submitting ? 'Saving...' : 'Add Stakeholder'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showRequestFor && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Request Service from {showRequestFor.name}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRequestFor(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Service Type</label>
                                    <input type="text" className="form-control" value={requestForm.service_type} onChange={(e) => setRequestForm(f => ({ ...f, service_type: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Priority</label>
                                    <select className="form-select" value={requestForm.priority} onChange={(e) => setRequestForm(f => ({ ...f, priority: e.target.value }))}>
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" rows={4} value={requestForm.description} onChange={(e) => setRequestForm(f => ({ ...f, description: e.target.value }))}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowRequestFor(null)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitRequest}>Submit Request</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StakeholderCenter;
